import React, { useState } from 'react';
import algoliasearch from 'algoliasearch/lite';
import {
  Configure,
  Hits,
  InstantSearch,
  Pagination,
  SearchBox,
  RefinementList,
  Snippet
} from 'react-instantsearch';

import { Panel } from './Panel';

import type { Hit } from 'instantsearch.js';

import './App.css';

import TypesenseInstantSearchAdapter from "typesense-instantsearch-adapter";

const typesenseInstantsearchAdapter = new TypesenseInstantSearchAdapter({
  server: {
    apiKey: "5ce8ab8c-d907-4c59-99ec-d0ca5c192075", // Be sure to use the search-only-api-key
    nodes: [
      {
        host: "adambrain-api.teller.run",
        port: 443,
        protocol: "https"
      }
    ]
  },
  // The following parameters are directly passed to Typesense's search API endpoint.
  //  So you can pass any parameters supported by the search endpoint below.
  //  query_by is required.
  additionalSearchParameters: {
    query_by: "content"
  }
});
const searchClient = typesenseInstantsearchAdapter.searchClient;

const future = { preserveSharedStateOnUnmount: true };

export function App() {
  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-white shadow-sm py-4">
        <h1 className="text-3xl font-bold text-center">
          <a href="/" className="text-blue-600 hover:text-blue-800">Adam Posts</a>
        </h1>

      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <InstantSearch
          searchClient={searchClient}
          indexName="posts"
          future={future}
        >
          <div className="grid grid-cols-1 gap-8 md:grid-cols-4">
            <div className="md:col-span-1">
              <div className="search-panel__filters">
                <div className="space-y-4">
                  <div className="p-4 bg-white rounded shadow">
                    <h3 className="font-medium mb-2">Post Type</h3>
                    <RefinementList attribute="postType" />
                  </div>
                  <div className="p-4 bg-white rounded shadow">
                    <h3 className="font-medium mb-2">Year</h3>
                    <RefinementList attribute="year" />
                  </div>
                  <div className="p-4 bg-white rounded shadow">
                    <h3 className="font-medium mb-2">Tags</h3>
                    <RefinementList attribute="tags" />
                  </div>
                </div>
              </div>
            </div>

            <div className="md:col-span-3">
              <Configure hitsPerPage={8} />
              <SearchBox placeholder="" className="mb-6" />
              <Hits hitComponent={Hit} />
              <div className="mt-8">
                <Pagination />
              </div>
            </div>
          </div>
        </InstantSearch>
      </div>
    </div>
  );
}

type HitProps = {
  hit: Hit;
};

function Hit({ hit }: HitProps) {
  const [expanded, setExpanded] = useState(false);

  return (
    <article >
      <div className="flex flex-col gap-4">
        <div className="flex items-start gap-3">
          <img src={hit.avatar} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0 min-w-[2.5rem] sticky top-0" />
          <div className="w-full">
            <p className="text-gray-700 text-base leading-relaxed whitespace-pre-line">
              {expanded ? hit.content : hit.previewText}
              <Snippet attribute="content" hit={hit} />
            </p>
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-blue-500 hover:text-blue-700 text-sm mt-2"
            >
              {expanded ? 'See less' : 'See more'}
            </button>
            <div className="relative group">
              <button className="text-gray-400 text-sm mt-1 hover:text-gray-600">
                <span>Summary</span>
              </button>
              <div className="absolute left-0 mt-2 p-4 bg-white rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10 w-96 border border-gray-200">
                <div className="text-gray-700 text-sm">{hit.summary}</div>
                <div className="absolute -top-2 left-4 w-4 h-4 bg-white transform rotate-45 border-l border-t border-gray-200"></div>
              </div>
            </div>
          </div>
          <div className="flex justify-end">
            {hit.images?.[0] && (
              <img src={hit.images[0]} alt="" className="w-24 h-24 object-cover rounded" />
            )}
          </div>
        </div>
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <button className="hover:bg-gray-100 p-2 rounded-full">
              <span className="flex items-center gap-1">
                <span>👍</span>
                <span>{hit.likes}</span>
              </span>
            </button>
            <button className="hover:bg-gray-100 p-2 rounded-full">
              <span className="flex items-center gap-1">
                <span>💬</span>
                <span>{hit.comments}</span>
              </span>
            </button>
            <button className="hover:bg-gray-100 p-2 rounded-full">
              <span className="flex items-center gap-1">
                <span>🔄</span>
                <span>{hit.shares}</span>

              </span>
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-gray-500 text-sm">
            {hit.tags?.map((tag) => (
              <span key={tag} className="bg-gray-100 px-2 py-1 rounded-full text-xs">
                {tag}
              </span>
            ))}
            <span>{new Date(hit.createdAt).toLocaleDateString()}</span>
            <a href={hit.url}
               target="_blank"
               rel="noopener noreferrer"
               className="hover:bg-gray-100 p-2 rounded-full">
              <span>↗️ View Post</span>
            </a>
          </div>
        </div>
      </div>
    </article>
  );
}
